import type { Message } from './use-chat';

/** Keep loaded history only when the refreshed tail proves the windows overlap. */
export function refreshHistoryWindow(current: Message[], latest: Message[], olderCursor: string | null,
  previousCursor: string | null, preserveEarlier: boolean): { messages: Message[]; olderCursor: string | null } {
  if (!preserveEarlier || latest.length === 0) return { messages: latest, olderCursor };
  const first = latest[0].finalId ?? latest[0].id;
  const overlap = current.findIndex(message => (message.finalId ?? message.id) === first);
  if (overlap < 0) return { messages: latest, olderCursor };
  return { messages: [...current.slice(0, overlap), ...latest], olderCursor: previousCursor };
}

/**
 * Carry the live THINKING rows across a window refresh. The window serves only rows that draw a bubble, and
 * a duplexer push is what triggers the refresh, so without this every pushed thinking line was wiped ~600ms
 * after it landed and the indicator fell back to the default line. Only rows newer than everything the
 * window returned survive: once the reply is persisted it is newer than them, so they cannot caption it.
 */
export function keepLiveThinking(current: Message[], refreshed: Message[]): Message[] {
  const newest = refreshed.reduce((max, message) => Math.max(max, timeOf(message)), -Infinity);
  const shown = new Set(refreshed.flatMap(message => [message.id, message.finalId]).filter(Boolean));
  const live = current.filter(message => message.role === 'THINKING' && !shown.has(message.id) && timeOf(message) > newest);
  return live.length === 0 ? refreshed : [...refreshed, ...live];
}

function timeOf(message: Message): number {
  const ms = message.createdDate ? Date.parse(message.createdDate) : NaN;
  return Number.isFinite(ms) ? ms : -Infinity;
}

export function prependHistoryWindow(current: Message[], earlier: Message[]): Message[] {
  const ids = new Set(current.flatMap(message => [message.id, message.finalId]).filter(Boolean));
  return [...earlier.filter(message => !ids.has(message.finalId ?? message.id)), ...current];
}

/** Warm navigation stores one bounded page, without replaying streaming animations. */
export function cachedHistoryWindow(messages: Message[]): Message[] {
  return messages.slice(-40).map(message => message.animate ? { ...message, animate: false } : message);
}
